import { isUtcZone } from '../entropy/estimates';
import type { RandomizationState } from './randomization';
import type { Collector, Signal } from './types';
import { prop, safe, signal, sleep, unavailable } from './util';

const BAIT_CLASSES = 'adsbox ad-banner ad-placeholder pub_300x250 pub_728x90 text-ad textAd text_ads banner-ads adsbygoogle';
const PERMISSIONS = ['geolocation', 'notifications', 'camera', 'microphone', 'clipboard-read'] as const;

async function adBlockerActive(): Promise<boolean> {
  const bait = document.createElement('div');
  bait.className = BAIT_CLASSES;
  bait.id = 'ad-banner-top';
  bait.setAttribute('aria-hidden', 'true');
  bait.textContent = ' ';
  bait.style.setProperty('position', 'absolute');
  bait.style.setProperty('left', '-9999px');
  bait.style.setProperty('width', '300px');
  bait.style.setProperty('height', '250px');
  document.body.appendChild(bait);
  try {
    // Cosmetic filters are applied asynchronously by extensions.
    await sleep(300);
    if (!bait.isConnected) return true;
    const cs = getComputedStyle(bait);
    return bait.offsetHeight === 0 || cs.display === 'none' || cs.visibility === 'hidden' || cs.opacity === '0';
  } finally {
    bait.remove();
  }
}

export const privacyCollector: Collector = {
  id: 'privacy',
  title: 'Privacy & blocking',
  description: 'Content blockers, permission states, privacy signals, and a summary of anti-fingerprinting protections detected across the report.',
  async collect() {
    const nav = typeof navigator === 'undefined' ? undefined : navigator;
    return Promise.all([
      safe('privacy.adblock', 'Content blocker', async () => {
        if (typeof document === 'undefined' || !document.body) return unavailable('privacy.adblock', 'Content blocker', 'No document');
        const blocked = await adBlockerActive();
        return signal('privacy.adblock', 'Content blocker', blocked, {
          display: blocked ? 'Detected (a bait element with ad-like class names was hidden)' : 'Not detected',
        });
      }),

      safe('privacy.permissions', 'Permission states', async () => {
        if (!nav?.permissions?.query) return unavailable('privacy.permissions', 'Permission states', 'Permissions API not available');
        const entries = await Promise.all(
          PERMISSIONS.map(async (name) => {
            try {
              // Query only. This never shows a prompt.
              const status = await nav.permissions.query({ name: name as PermissionName });
              return [name, status.state] as const;
            } catch {
              return [name, 'unsupported'] as const;
            }
          }),
        );
        return signal('privacy.permissions', 'Permission states', Object.fromEntries(entries), {
          display: entries.map(([n, s]) => `${n}: ${s}`).join(' · '),
        });
      }),

      safe('privacy.trackingHints', 'Privacy signals sent', () => {
        const gpc = prop<boolean>(nav, 'globalPrivacyControl');
        const dnt = nav?.doNotTrack ?? null;
        return signal('privacy.trackingHints', 'Privacy signals sent', { gpc: gpc ?? null, dnt }, {
          display: `Global Privacy Control: ${gpc === undefined ? 'not supported' : gpc ? 'on' : 'off'} · Do Not Track: ${dnt === '1' ? 'on' : dnt === '0' ? 'off' : 'unset'}`,
          note: 'The Sec-GPC request header cannot be read by page scripts, so only navigator.globalPrivacyControl is shown.',
        });
      }),
    ]);
  },
};

// ── Cross-category protection summary ────────────────────────────────────────────────────────────

export interface Protection {
  id: string;
  label: string;
  detail: string;
  /** strong: defeats a high-entropy signal · partial: reduces one · signal: a privacy setting, not a fingerprint defence */
  strength: 'strong' | 'partial' | 'signal';
}

type SignalMap = ReadonlyMap<string, Signal>;
const state = (m: SignalMap, id: string): RandomizationState | undefined => prop<RandomizationState>(m.get(id)?.value, 'state');

export function timerResolutionMs(m: SignalMap): number | null {
  const v = prop<number>(m.get('misc.timer')?.value, 'resolutionMs');
  return typeof v === 'number' ? v : null;
}

/**
 * Firefox's resistFingerprinting and Tor Browser make many values identical for every user.
 * Detected when a Firefox-family browser shows at least two of its hallmarks.
 */
export function uniformModeLikely(m: SignalMap): boolean {
  const ua = String(m.get('nav.userAgent')?.value ?? '');
  if (!/Firefox\//.test(ua)) return false;
  const hallmarks = [
    isUtcZone(String(m.get('locale.timezone')?.value ?? '')),
    prop<boolean>(m.get('screen.spoofCheck')?.value, 'spoofed') === true,
    (timerResolutionMs(m) ?? 0) >= 16,
  ];
  return hallmarks.filter(Boolean).length >= 2;
}

export function detectProtections(m: SignalMap, changedBetweenRuns: string[] = []): Protection[] {
  const out: Protection[] = [];
  const add = (id: string, label: string, detail: string, strength: Protection['strength']) => out.push({ id, label, detail, strength });

  if (uniformModeLikely(m))
    add('uniform', 'Uniform fingerprint mode', 'Looks like Tor Browser or Firefox with resistFingerprinting: many values are identical for every user of that mode.', 'strong');

  const surfaces: Array<[string, string]> = [
    ['canvas.randomization', 'Canvas'],
    ['webgl.randomization', 'WebGL'],
    ['audio.randomization', 'Audio'],
  ];
  for (const [id, name] of surfaces) {
    const s = state(m, id);
    if (s === 'randomized') add(id, `${name} randomization active`, `${name} output changed between two identical renders.`, 'strong');
    else if (s === 'noise') add(id, `${name} noise injection active`, `A known ${name.toLowerCase()} test pattern came back altered.`, 'strong');
    else if (s === 'blocked') add(id, `${name} readback blocked`, `The browser returned blank ${name.toLowerCase()} data.`, 'strong');
  }

  const webglSupport = m.get('webgl.support');
  if (webglSupport && webglSupport.available === false) add('webgl.disabled', 'WebGL disabled', 'No WebGL context could be created.', 'strong');
  const renderer = m.get('webgl.renderer');
  if (renderer?.available && renderer.note?.startsWith('Generic')) add('webgl.masked', 'GPU name masked', `WebGL reports a generic renderer (“${String(renderer.value)}”).`, 'partial');

  const rtcAvailable = m.get('webrtc.available');
  const exposure = prop<string>(m.get('webrtc.localIps')?.value, 'exposure');
  if (rtcAvailable && rtcAvailable.value === false) add('webrtc.disabled', 'WebRTC disabled', 'RTCPeerConnection is not available, so no IP addresses can leak through it.', 'strong');
  else if (exposure === 'mdns') add('webrtc.mdns', 'WebRTC local IPs hidden', 'Local addresses are replaced by random .local names (mDNS).', 'partial');
  else if (exposure === 'none') add('webrtc.none', 'WebRTC exposes no local candidates', 'No host candidates were gathered without a permission grant.', 'partial');

  if (prop<boolean>(m.get('screen.spoofCheck')?.value, 'spoofed')) add('screen.spoofed', 'Screen size rounded or spoofed', 'Your reported screen matches a rounded window size.', 'strong');
  if (isUtcZone(String(m.get('locale.timezone')?.value ?? ''))) add('tz.utc', 'Time zone reported as UTC', 'Either you live on UTC or your browser hides your real time zone.', 'partial');

  const res = timerResolutionMs(m);
  if (res !== null && res >= 16) add('timer.coarse', 'Timer precision heavily reduced', `performance.now() ticks every ~${res.toFixed(1)} ms.`, 'partial');

  const fonts = m.get('fonts.osGuess');
  if (fonts?.available && prop<number>(fonts.value, 'extras') === 0 && m.get('fonts.list')?.available)
    add('fonts.restricted', 'Only default fonts visible', 'No fonts beyond your OS defaults are visible to pages, either because none are installed or because your browser hides them.', 'partial');

  if (prop<boolean>(m.get('nav.vendorGlobals')?.value, 'brave'))
    add('brave', 'Brave Shields', 'Brave randomizes ("farbles") several APIs per site and per session.', 'partial');

  if (changedBetweenRuns.length)
    add('volatile', 'Values change between reads', `${changedBetweenRuns.length} signal(s) changed between two samples a moment apart.`, 'strong');

  if (m.get('privacy.adblock')?.value === true) add('adblock', 'Content blocker active', 'Blocks many tracking scripts, though its presence is itself detectable.', 'signal');
  if (m.get('nav.globalPrivacyControl')?.value === true) add('gpc', 'Global Privacy Control on', 'Asks sites not to sell or share your data; legally binding in some US states.', 'signal');

  return out;
}

export function protectionsSignal(protections: Protection[]): Signal {
  const defences = protections.filter((p) => p.strength !== 'signal');
  return signal('privacy.antiFingerprinting', 'Anti-fingerprinting summary', protections.map((p) => p.id), {
    display: defences.length ? defences.map((p) => `• ${p.label}`).join('\n') : 'No anti-fingerprinting protections detected',
  });
}
